import { useDragLayer, useDrop } from 'react-dnd';
import { useDispatch, useSelector } from 'react-redux';
import { FunctionComponent, useEffect, useState } from 'react';
import { actionCreators } from '../../reducers/templateEditor';
import { StateShape as ReduxStateShape } from '../../reducers';
import { Slide } from './index';

interface LayerItemProps {
    layer: Slide;
    onClickItem?: (id: string) => void;
    onClickAccordion?: () => void;
    isActive?: boolean;
    isClosed?: boolean;
    hasChildren?: boolean;
}

const getParents = (data: Slide[], id: string) => {
    const parents: string[] = [];
    const getNodes = (result: Slide[], node: Slide) => {
        if (node.id === id) {
            result.push(node);
            parents.push(node.id);
            return result;
        }

        if (Array.isArray(node.children)) {
            const children = node.children.reduce(getNodes, []);

            if (children.length) {
                result.push({ ...node, children });
                parents.push(node.id);
            }
        } else if (node.children && Object.keys(node.children).length > 0) {
            Object.keys(node.children).forEach((ch) => {
                const children = node.children[ch].reduce(getNodes, []);

                if (children.length) {
                    result.push({ ...node, children: { [ch]: [...children] } });
                    parents.push(node.id);
                }
            });
        }

        return result;
    };

    data.reduce(getNodes, []);
    return parents;
};

interface NewSlideInputProps {
    index: number;
}

const NewSlideInput: FunctionComponent<React.PropsWithChildren<NewSlideInputProps>> = ({
    index,
}) => {
    const dispatch = useDispatch();

    const handleDrop = (item: {
        id: string;
        widgetUUID: string;
        dragMode: 'move' | 'copy' | 'create';
    }) => {
        if (item.dragMode === 'move') {
            dispatch(actionCreators.moveSlide(item.widgetUUID, index));

            setTimeout(() => {
                const el = document.getElementById(`slide-${item.widgetUUID}`);
                const slidesList = document.getElementById('slides-list');

                if (el && slidesList) {
                    const elRect = el.getBoundingClientRect();
                    const slidesListRect = slidesList.getBoundingClientRect();

                    const top = elRect.top - slidesListRect.top + 45;
                    const left = elRect.left - slidesListRect.left + elRect.width - 5;

                    const event = new CustomEvent('slide-menu-position', {
                        detail: { top, left },
                    });

                    window.dispatchEvent(event);
                }
            }, 200);
        }
    };

    const [{ isOver }, drop] = useDrop(() => ({
        accept: 'SLIDE',
        drop: handleDrop,
        collect: (monitor) => ({
            isOver: monitor.isOver(),
            canDrop: monitor.canDrop(),
        }),
    }));

    return (
        <div
            ref={drop}
            className="add-slide-container"
            style={{ backgroundColor: isOver ? '#ededed' : 'white' }}
            onClick={(e) => {
                e.stopPropagation();
            }}
        >
            <img src="add.svg" className="add-slide-icon" alt="add widget" />
        </div>
    );
};

const SlideItem = (props: LayerItemProps) => {
    const { layer, isActive } = props;
    const snapshots = useSelector((state: ReduxStateShape) => state.templateEditor.snapshots);

    return (
        <li
            className={`slide-item  ${isActive ? 'layer-active' : ''} ${
                !layer.id ? 'layer-non-clickable' : ''
            }`}
            id={`slide-${layer.id}`}
        >
            <div>
                {snapshots?.[layer.id] && (
                    <img src={snapshots[layer.id]} alt={`Preview for ${layer.id}`} />
                )}
            </div>
        </li>
    );
};

interface SlidesProps {
    layers: Slide[];
    initialLayers: Slide[];
    closed?: boolean;
}

const Slides = (props: SlidesProps) => {
    const { initialLayers, layers, closed } = props;
    const dispatch = useDispatch();
    const [closedNodes, setClosedNodes] = useState<string[]>([]);
    const { selectedId } = useSelector((state: ReduxStateShape) => ({ ...state.templateEditor }));

    const onClickListItem = (id: string) => {
        dispatch(actionCreators.selectElement(id));
        const el = document.getElementById(`comp-${id}`);
        el?.scrollIntoView({ behavior: 'smooth' });
    };

    const onClickAccordion = (id: string) => {
        if (closedNodes?.includes(id)) {
            setClosedNodes([...closedNodes?.filter((n) => n !== id)]);
        } else {
            setClosedNodes([...closedNodes, id]);
        }
    };

    useEffect(() => {
        if (selectedId) {
            const parentsIds = getParents(initialLayers, selectedId);
            setClosedNodes(closedNodes.filter((node) => !parentsIds.includes(node)));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedId]);

    const { isDragging: isDraggingWidget, itemType } = useDragLayer((monitor) => ({
        isDragging: monitor.isDragging(),
        itemType: monitor.getItemType(),
    }));

    const showNewSlideInput = isDraggingWidget && itemType === 'SLIDE';

    return (
        <div className={`layers ${closed ? 'closed' : ''}`}>
            {layers?.length > 0 &&
                layers?.[0]?.children?.map((layer: Slide, index: number) => {
                    const isNotSelected = selectedId && selectedId !== layer.id;
                    return layer.widgetId === 'Page' ? (
                        <ul key={`${layer.id}-${index}`} className={`layer`}>
                            {showNewSlideInput && isNotSelected && index === 0 && (
                                <NewSlideInput index={0} />
                            )}
                            <SlideItem
                                layer={layer}
                                onClickItem={onClickListItem}
                                isActive={layer.id === selectedId}
                                hasChildren={!!layer.children}
                                isClosed={closedNodes.includes(layer.id)}
                                onClickAccordion={() => onClickAccordion(layer.id)}
                            />
                            {showNewSlideInput && isNotSelected && (
                                <NewSlideInput index={index + 1} />
                            )}
                        </ul>
                    ) : (
                        <></>
                    );
                })}
        </div>
    );
};

export default Slides;
